import { Package } from "lucide-react";
import type { ProductListRow } from "@stackje/site-builder";
import type { ShopIndex } from "../../api/contract";
import { formatMoney } from "../../lib/shopMoney";
import { StatusChip } from "../area";

/** The kind of product, in a few words: "Physical", "Download" or "Physical + download". */
function productKind(product: ProductListRow): string {
  return `${product.physical ? "Physical" : "Download"}${product.physical && product.downloads ? " + download" : ""}`;
}

function categoryNames(product: ProductListRow, shop: ShopIndex): string {
  return product.categories.map((id) => shop.categories.find((item) => item.id === id)?.name).filter(Boolean).join(", ") || "Uncategorised";
}

function ProductRow({ product, shop, shopSite, language, selected, onSelect, onOpen }: { product: ProductListRow; shop: ShopIndex; shopSite: string; language: string; selected: boolean; onSelect: () => void; onOpen: () => void }) {
  const photo = product.photo;
  return <div className="bw-shop-row" role="row">
    <input type="checkbox" aria-label={`Select ${product.name}`} checked={selected} onChange={onSelect} />
    <button type="button" className="bw-shop-product-link" onClick={onOpen}>
      {photo ? <img src={`/_builder/sites/${shopSite}/shop/products/${product.id}/photos/${photo.assetId}/${photo.version}`} alt="" /> : <span className="bw-shop-thumbnail"><Package size={20} aria-hidden="true" /></span>}
      <span><strong>{product.name}</strong><small>{productKind(product)}</small></span>
    </button>
    <span>{categoryNames(product, shop)}</span>
    <span>{product.priceRange ? product.priceRange.min === product.priceRange.max ? formatMoney(product.priceRange.min, shop.settings!.currency, language) : `${formatMoney(product.priceRange.min, shop.settings!.currency, language)} – ${formatMoney(product.priceRange.max, shop.settings!.currency, language)}` : "Price needed"}</span>
    <span>{product.stock}</span>
    <StatusChip status={product.status} />
  </div>;
}

/** The products as rows with a tick box each. On a phone only the name, kind and status stay. */
export function ProductTable({ rows, shop, siteId, language, selected, onSelected, onOpen }: { rows: ProductListRow[]; shop: ShopIndex; siteId: string; language: string; selected: string[]; onSelected: (rows: ProductListRow[], checked: boolean) => void; onOpen: (id: string) => void }) {
  const toggle = (row: ProductListRow) => onSelected([row], !selected.includes(row.id));
  return <div className="bw-shop-table" role="table" aria-label="Products">
    <div className="bw-shop-row bw-shop-table-head" role="row">
      <input type="checkbox" aria-label="Select all" checked={rows.every((row) => selected.includes(row.id))} onChange={(event) => onSelected(rows, event.target.checked)} />
      <span>Product</span><span>Category</span><span>Price</span><span>Stock</span><span>Status</span>
    </div>
    {rows.map((row) => <ProductRow key={row.id} product={row} shop={shop} shopSite={siteId} language={language} selected={selected.includes(row.id)} onSelect={() => toggle(row)} onOpen={() => onOpen(row.id)} />)}
  </div>;
}
